import { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import {
  motionTransition,
  usePrefersReducedMotion,
} from 'ts-components/PiChat/motion';
import type { FocusPhase } from './motion';

const PlaceholderRoot = styled.div<{ $draggable: boolean }>`
  &&& {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    border: 1px dashed var(--color-border-medium);
    border-radius: 8px;
    background: var(--color-background-lightest);
    color: var(--color-font-placeholder);
    opacity: 1;
    cursor: ${({ $draggable }) => ($draggable ? 'move' : 'default')};
    user-select: none;
    overflow: hidden;
    z-index: 3;
  }
`;

const Label = styled.span`
  max-width: 100%;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
`;

export type PlaceholderDashlet = {
  id: string;
  name?: string | null;
  config?: { content?: { name?: unknown } | null } | null;
};

export type AiFocusPlaceholderProps = {
  dashlet: PlaceholderDashlet;
  isDraggable?: boolean;
  phase?: FocusPhase;
  onSettled?: (phase: FocusPhase) => void;
};

export const placeholderTargetOpacity = (phase: FocusPhase | undefined) =>
  phase === 'exiting' ? 0 : 1;

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const PLUGIN_BLOCK_KEY = /^appbuilder:[^/]+\/([^/]+)$/;

const readableName = (name: string): string => {
  const match = PLUGIN_BLOCK_KEY.exec(name);
  if (!match) return name;
  const words = match[1].replace(/[_-]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : name;
};

export const getPlaceholderName = (
  dashlet: PlaceholderDashlet
): string | null => {
  const contentName = dashlet.config?.content?.name;
  if (hasText(dashlet.name)) {
    const name = dashlet.name.trim();
    if (PLUGIN_BLOCK_KEY.test(name) && hasText(contentName)) {
      return contentName.trim();
    }
    return readableName(name);
  }
  return hasText(contentName) ? contentName.trim() : null;
};

export const AiFocusPlaceholder: FC<AiFocusPlaceholderProps> = ({
  dashlet,
  isDraggable = false,
  phase,
  onSettled,
}) => {
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const targetOpacity = placeholderTargetOpacity(phase);
  const [opacity, setOpacity] = useState(
    phase === 'entering' && !reducedMotion ? 0 : targetOpacity
  );
  useEffect(() => {
    if (opacity === targetOpacity) return undefined;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setOpacity(targetOpacity));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [opacity, targetOpacity]);
  const name = getPlaceholderName(dashlet) ?? t('Block');
  return (
    <PlaceholderRoot
      className="drag-handle"
      $draggable={isDraggable}
      data-qa-id="ai-focus-placeholder"
      data-qa-dashlet-id={dashlet.id}
      data-qa-phase={phase ?? 'focused'}
      style={{
        opacity,
        transition: motionTransition(['opacity'], reducedMotion),
      }}
      title={name ?? undefined}
      onTransitionEnd={(event) => {
        if (
          phase &&
          event.target === event.currentTarget &&
          event.propertyName === 'opacity' &&
          opacity === targetOpacity
        ) {
          onSettled?.(phase);
        }
      }}
    >
      <Label>{name}</Label>
    </PlaceholderRoot>
  );
};
